import React, { ComponentProps, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import Loading from 'components/commons/loading/Loading';
import {
  HowToSaveType,
  Target,
  History,
  TargetHistoryTransitionData,
  howToSaveCash,
} from 'components/features/target-achievement-support/utils/type';
import {
  ExtraTimeSeriesData,
  TimeSeriesDatum,
} from 'components/features/home/assetStuffing/AssetStuffing';
import { getTargetHistory } from 'config/api';
import { Console } from 'utils/logger';
import { DropDown } from '../../../commons/DropDown';
import { dropDownClassNames } from './dropdownStyle';
import { MonthlyAICheer } from './rangeTab/MonthlyAICheer';
import { RemainAmount } from './rangeTab/RemainAmount';
import { TransitionChart } from './TransitionChart';

const toString = ({ month, year }: { year: number; month: number }): string =>
  `${year.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}`;

const fromString = (
  v: string
):
  | { tag: 'Just'; value: { year: number; month: number } }
  | { tag: 'Nothing' } => {
  Console.debug(v);
  const regex = /^(\d{4})\/(\d{2})$/;
  const result = regex.exec(v);
  if (!result) return { tag: 'Nothing' };
  const year = result[1] ? Number(result[1]) : null;
  const month = result[2] ? Number(result[2]) : null;
  if (!year || !month) return { tag: 'Nothing' };
  if (month < 0 || month > 12) return { tag: 'Nothing' };
  return { tag: 'Just', value: { year, month } };
};

const toLabel = ({ month, year }: { year: number; month: number }): string =>
  `${year}年${month}月`;

const MonthDropDown = ({
  setState,
  state,
  states,
}: {
  state: { year: number; month: number };
  setState: React.Dispatch<
    React.SetStateAction<{ year: number; month: number }>
  >;
  states: { year: number; month: number }[];
}): React.ReactElement => {
  return (
    <>
      <DropDown
        {...{
          classNames: dropDownClassNames,
          dropdownIcon: 'arrow_drop_down',
          toString_: toString,
          fromString,
          setState,
          state,
          states,
          toLabel,
        }}
      />
    </>
  );
};

const getEndDate = (year: number, month: number): Date => {
  const endDate = new Date(
    new Date(year, (month - 1 + 1) % 12, 1).getTime() - 1000 * 60 * 60 * 24
  );
  return endDate;
};

const MonthRemainAmount = ({
  currentAmount,
  targetAmount,
  month,
  className,
}: {
  currentAmount: number;
  targetAmount: number;
  month: number;
  className?: string;
}): React.ReactElement => {
  return (
    <RemainAmount
      {...{
        currentAmount,
        targetAmount,
        limit: {
          period: 'month',
          value: month,
        },
        className,
      }}
    />
  );
};

const MonthAICheer = ({
  percentage,
  title,
  achieved,
  currentAmount,
  targetAmount,
  targetStartDate,
  targetEndDate,
  requiredYield,
  goHowToIncreaseAccumulationAmount,
  className,
}: Omit<ComponentProps<typeof MonthlyAICheer>, 'period'> & {
  className?: string;
}): React.ReactElement => {
  return (
    <div
      className={`rounded-lg flex flex-row items-start gap-4 border border-bluegray-300 bg-bluegray-100 p-4 ${className ??
        ''}`}>
      <MonthlyAICheer
        {...{
          percentage,
          title,
          achieved,
          currentAmount,
          targetAmount,
          targetStartDate,
          targetEndDate,
          requiredYield,
          goHowToIncreaseAccumulationAmount,
        }}
      />
    </div>
  );
};

const toStates = ({ from: start, to: end }: { from: Date; to: Date }) => {
  const toYearMonth = (date: Date) => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
  });
  const toAbsMonths = (date: Date) => date.getFullYear() * 12 + date.getMonth();
  return Array.from({
    length: toAbsMonths(end) - toAbsMonths(start) + 1,
  }).map((_, i) => {
    const newMonth = toYearMonth(start).month + i;
    return {
      year: toYearMonth(start).year + Math.floor((newMonth - 1) / 12),
      month: ((newMonth - 1) % 12) + 1,
    };
  });
};

export const ChartLoading = () => {
  return (
    <div
      className="flex justify-center bg-white py-4"
      style={{ height: '350px' }}>
      <Loading loading circularLoading={true} />
    </div>
  );
};

type Props = {
  target: Target;
  targetHowToSaveType: HowToSaveType;
  goHowToIncreaseAccumulationAmount: () => void;
};

export const Month = (props: Props): React.ReactElement => {
  const [state, setState] = React.useState<{ year: number; month: number }>({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  const { data: history, refetch, isLoading, isRefetching } = useQuery(
    'monthTargethistory',
    () => getTargetHistoryAPI(props.target.id, state.year, state.month),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    refetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const isFailed = history === null || history === undefined;

  const aiCheer = React.useMemo(() => {
    if (isFailed) return null;
    const currentAmount = history.achievementAmount;
    const targetAmountForDisplay = history.targetAmount;

    const achieved = props.target.achieved;
    const targetStartDate = new Date(props.target.startDate);
    const targetEndDate = new Date(props.target.endDate);
    const requiredYield =
      props.target.reserveAmount !== null &&
      props.target.excludeAmount !== null &&
      props.target.accountList !== null
        ? null
        : props.targetHowToSaveType === howToSaveCash
        ? null
        : (annualYield => Math.ceil(annualYield * 1000) / 10)(
            props.target.yield
          );

    const num = (currentAmount / targetAmountForDisplay) * 100;
    const percentage = Number.isNaN(num) ? 0 : num;

    return (
      <MonthAICheer
        percentage={percentage}
        title={props.target.title}
        achieved={achieved}
        currentAmount={currentAmount}
        targetAmount={targetAmountForDisplay}
        targetStartDate={targetStartDate}
        targetEndDate={targetEndDate}
        requiredYield={requiredYield}
        goHowToIncreaseAccumulationAmount={
          props.goHowToIncreaseAccumulationAmount
        }
        className="mt-4 mx-4"
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFailed]);

  const range = useMemo(() => {
    if (isFailed)
      return {
        start: null,
        end: null,
      };

    const from = new Date(history.from);
    const to = new Date(history.to);

    return {
      start: Math.max(1, from.getDate()),
      end: Math.min(
        to.getDate(),
        getEndDate(state.year, state.month).getDate()
      ),
    };
  }, [isFailed, history, state.year, state.month]);

  const chartIncorrectData = useMemo(() => {
    if (isFailed) return null;

    const transitions = history.transitions as TargetHistoryTransitionData[];
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: transitions.map(it => {
        return {
          value: it.incorrect,
          date: new Date(it.date),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [history, isFailed]);

  if (isFailed || isLoading || isRefetching || chartIncorrectData === null)
    return <ChartLoading />;

  const startDate = new Date(props.target.startDate);
  const endDate = new Date(props.target.endDate);

  const states = toStates({
    from: startDate,
    to: new Date(),
  });

  const currentAmount = history.achievementAmount;

  const targetAmountForDisplay = history.targetAmount;

  const chartData =
    history.transitions[history.transitions.length - 1]?.benchmarkAmount ??
    history.targetAmount;

  const transitions = history.transitions as TargetHistoryTransitionData[];

  return (
    <>
      <div>
        <div className="bg-white py-4">
          <div className="flex flex-col items-center justify-center">
            <div className="flex flex-row items-center justify-center ml-3">
              <MonthDropDown {...{ state, setState, states }} />
            </div>
            <div className={`text-bluegray-700 text-sm`}>
              {range.start}日〜{range.end}日
            </div>
          </div>
          <MonthRemainAmount
            {...{
              currentAmount,
              month: state.month,
              targetAmount: targetAmountForDisplay,
            }}
            className="mt-2"
          />
          <TransitionChart
            dateFormatter={date => `${date.getMonth() + 1}/${date.getDate()}`}
            targetDate={endDate}
            data={{
              dataRange: {
                from: startDate,
                to: endDate,
              },
              achievements: transitions.map(item => {
                return {
                  achievement: item.assetAmount,
                  date: new Date(item.date),
                };
              }),
              benchmarks: transitions.map(item => ({
                benchmark: item.benchmarkAmount,
                date: new Date(item.date),
              })),
              targetAmount: chartData,
              extraData: chartIncorrectData,
            }}
          />
          {aiCheer}
        </div>
      </div>
    </>
  );
};

const getTargetHistoryAPI = async (id: string, year: number, month: number) => {
  const period = `${year}${month.toString().padStart(2, '0')}`;
  const ret = await getTargetHistory(id, period);

  return {
    id: ret.ID,
    achievementAmount: ret.AchievementAmount,
    targetAmount: ret.TargetAmount,
    from: ret.From,
    to: ret.To,
    transitions: ret.Transitions.map(item => {
      return {
        assetAmount: item.AssetAmount,
        benchmarkAmount: item.BenchmarkAmount,
        date: item.Date,
        incorrect: item.Incorrect,
      };
    }),
  } as History;
};
